// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { registerIcons } from '@fluentui/react';
import { FluentThemeProvider } from "../../../../react-components/src";
import React, { createContext, useContext } from 'react';
import { LocalizationProvider } from '../localization';
import { DEFAULT_COMPOSITE_ICONS } from './icons';
/**
 * A base provider {@link React.Context} to wrap components with other required providers
 * (e.g. icons, FluentThemeProvider, LocalizationProvider).
 *
 * Required providers are only wrapped once, with all other instances only passing children.
 *
 * @private
 */
export const BaseProvider = (props) => {
    const { fluentTheme, rtl, locale } = props;
    /**
     * Pass only the children if we previously registered icons, and have previously wrapped the children in
     * FluentThemeProvider and LocalizationProvider
     */
    const alreadyWrapped = useBase();
    if (alreadyWrapped) {
        return React.createElement(React.Fragment, null, props.children);
    }
    /**
     * We register the default icon mappings merged with custom icons provided through props
     * to ensure all icons render correctly.
     */
    registerIcons({
        icons: Object.assign(Object.assign({}, DEFAULT_COMPOSITE_ICONS), props.icons)
    });
    const CompositeElement = React.createElement(FluentThemeProvider, { fluentTheme: fluentTheme, rtl: rtl },
        React.createElement("meta", { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0" }),
        props.children);
    const localizedElement = locale ? LocalizationProvider({
        locale,
        children: CompositeElement
    }) : CompositeElement;
    return React.createElement(BaseContext.Provider, { value: true }, localizedElement);
};
/**
 * @private
 */
const BaseContext = createContext(false);
/**
 * @private
 */
const useBase = () => useContext(BaseContext);
//# sourceMappingURL=BaseComposite.js.map